import React, { useRef } from "react";
import { cn } from "./cn";

/* Six-box verification code entry. */
export const OtpInput = ({
  value = "",
  onChange = () => {},
  length = 6,
  disabled = false,
  ariaLabel = "Verification code",
  className = "",
}) => {
  const refs = useRef([]);
  const chars = String(value || "").slice(0, length).split("");

  const setChar = (index, char) => {
    const next = Array.from({ length }, (_, i) => chars[i] || "");
    next[index] = char;
    onChange(next.join("").trim());
  };

  const focus = (index) => {
    const el = refs.current[index];
    if (el) el.focus();
  };

  const handleChange = (index) => (e) => {
    // Take the last digit typed, so overtyping a filled box replaces it.
    const digits = e.target.value.replace(/\D/g, "");
    if (!digits) {
      setChar(index, "");
      return;
    }
    const char = digits[digits.length - 1];
    setChar(index, char);
    if (index < length - 1) focus(index + 1);
  };

  const handleKeyDown = (index) => (e) => {
    if (e.key === "Backspace" && !chars[index] && index > 0) {
      e.preventDefault();
      setChar(index - 1, "");
      focus(index - 1);
    }
    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      focus(index - 1);
    }
    if (e.key === "ArrowRight" && index < length - 1) {
      e.preventDefault();
      focus(index + 1);
    }
  };

  /* Pasting a code should fill the row — the common case is copying it out of
     an email, which would otherwise drop every digit but the first. */
  const handlePaste = (e) => {
    const pasted = (e.clipboardData?.getData("text") || "").replace(/\D/g, "");
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted.slice(0, length));
    focus(Math.min(pasted.length, length - 1));
  };

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn("flex gap-1.5 sm:gap-2 justify-center", className)}
    >
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          disabled={disabled}
          aria-label={`Digit ${i + 1} of ${length}`}
          value={chars[i] || ""}
          onChange={handleChange(i)}
          onKeyDown={handleKeyDown(i)}
          onPaste={handlePaste}
          className={cn(
            // 52x60 on phones, per the design. Capped rather than fixed:
            // six 52px boxes plus gaps exceed a 320px viewport once the card's
            // padding is taken off, so they shrink instead of overflowing.
            "flex-1 min-w-0 max-w-[52px] h-[60px] sm:max-w-[56px] sm:h-14",
            "text-center rounded-xl bg-white",
            "border-[1.5px] border-[var(--line-2)] text-[19px] sm:text-[21px] font-semibold text-[var(--ink)] font-sans",
            "transition-[border-color,box-shadow] duration-150",
            "focus:outline-none focus:border-[var(--orange)] focus:shadow-[0_0_0_4px_var(--focus-ring)]",
            disabled && "opacity-60 cursor-not-allowed"
          )}
        />
      ))}
    </div>
  );
};

export default OtpInput;
